import { type ReactNode } from 'react';
import { cn } from '~/lib/cn';

/**
 * Form building blocks reused across the KYB steps. Kept here instead of
 * repeated inline so a change to the field density, focus ring, or error
 * placement lands in one spot.
 */

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5" htmlFor={htmlFor}>
      <span className="text-[13.5px] font-medium text-ink-primary">
        {label}
      </span>
      {hint && <span className="text-[11.5px] text-ink-watermark">{hint}</span>}
      {children}
      {error && <span className="text-[13px] text-stop">{error}</span>}
    </label>
  );
}

const INPUT_BASE =
  'w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary focus:border-ink-primary focus:bg-surface-slip focus:outline-none focus:ring-2 focus:ring-ink-primary/15 disabled:cursor-not-allowed disabled:opacity-55';

export function TextInput({
  value,
  onChange,
  invalid,
  ...rest
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'url';
  maxLength?: number;
  id?: string;
}): JSX.Element {
  return (
    <input
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(INPUT_BASE, 'h-9', invalid && 'border-stop/50')}
    />
  );
}

export function TextArea({
  value,
  onChange,
  rows = 3,
  invalid,
  ...rest
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  invalid?: boolean;
  placeholder?: string;
  maxLength?: number;
  id?: string;
}): JSX.Element {
  return (
    <textarea
      {...rest}
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      className={cn(INPUT_BASE, 'py-2 leading-[20px]', invalid && 'border-stop/50')}
    />
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  invalid,
  placeholder,
  id,
}: {
  value: T | '';
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  invalid?: boolean;
  placeholder?: string;
  id?: string;
}): JSX.Element {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className={cn(INPUT_BASE, 'h-9 appearance-none pr-8', invalid && 'border-stop/50')}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none' stroke='%23525252' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M1 1l4 4 4-4'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
      }}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function TwoCol({ children }: { children: ReactNode }): JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

export function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-rule-hairline bg-surface-slip p-6">
      <div className="mb-5">
        <h2 className="text-[15px] font-semibold text-ink-primary">{title}</h2>
        {description && (
          <p className="mt-1 text-[13.5px] leading-[18px] text-ink-secondary">
            {description}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}
